import { useAtomValue } from 'jotai';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { Button, Drawer, Spin, Table } from 'antd';
import { useColumns } from './columns';
import { PaymentAccount } from '~/types/payment';
import PaymentAccountForm from './components/PaymentAccountForm';
import { LoadingOutlined } from '@ant-design/icons';
import { usePaymentAccounts } from '../../hooks/usePaymentAccounts';
import { useQueryClient } from '@tanstack/react-query';

const ListPaymentAccountTable = ({
  openDrawer,
}: {
  openDrawer: (item?: PaymentAccount) => void;
}) => {
  const paymentAccounts = usePaymentAccounts();

  const sortedData = useMemo(() => {
    const items = [...paymentAccounts];
    items.sort((a, b) => a.sequence - b.sequence);
    return items;
  }, [paymentAccounts]);

  const columns = useColumns({ openDrawer });

  return <Table columns={columns} dataSource={sortedData} />;
};

const ListPaymentAccount = () => {
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState<PaymentAccount>();

  const openDrawer = useCallback((item?: PaymentAccount) => {
    setCurrentEdit(item);
    setIsDrawerOpen(true);
  }, []);

  const onFinish = () => {
    setIsDrawerOpen(false);
    setCurrentEdit(undefined);
    queryClient.invalidateQueries({ queryKey: ['fetchPaymentAccounts'] });
  };

  const onClickCreate = () => {
    setCurrentEdit(undefined);
    setIsDrawerOpen(true);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl">Payment Accounts</h1>
        <Button type="primary" onClick={onClickCreate}>
          + Create
        </Button>
      </div>
      <Suspense
        fallback={
          <div className="w-full flex items-center justify-center h-[300px]">
            <Spin indicator={<LoadingOutlined />} />
          </div>
        }
      >
        <ListPaymentAccountTable openDrawer={openDrawer} />
      </Suspense>
      <Drawer
        title={currentEdit ? 'Edit' : 'Create'}
        width={500}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        destroyOnClose
      >
        <PaymentAccountForm
          key={currentEdit ? currentEdit.paymentAccountId : 'create-new'}
          initialValue={currentEdit}
          onFinish={onFinish}
        />
      </Drawer>
    </div>
  );
};

export default ListPaymentAccount;
